/**
 * WhiteboardCaptureCoach — a live camera coach for whiteboard capture.
 *
 * People photograph walls from across the room in bad light, then find the
 * handwriting didn't survive the vision model's downscale. This opens the
 * device camera and, before any shot is taken, samples the live preview a few
 * times a second through the same pure quality assessment used post-capture
 * (whiteboardCaptureQuality) — telling the user in real time to add light, move
 * closer, or hold steady, and turning the shutter green only when the frame is
 * actually legible. Captures are handed back to the importer as data URLs; the
 * coach stays open so a wall can be walked in several shots.
 *
 * Pure-UI: all the math lives in the tested lib module; this is camera plumbing
 * plus a coaching banner.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, X, Check, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import { assessImageQuality, type CaptureQuality } from '../../../lib/whiteboardCaptureQuality';

const WORKING_LONG_EDGE = 512; // matches the post-capture quality check
const SAMPLE_INTERVAL_MS = 500;

interface Props {
  onCapture: (dataUrl: string) => void;
  onClose: () => void;
}

export function WhiteboardCaptureCoach({ onCapture, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const workCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const [status, setStatus] = useState<'starting' | 'live' | 'error'>('starting');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [quality, setQuality] = useState<CaptureQuality | null>(null);
  const [captured, setCaptured] = useState(0);
  const [flash, setFlash] = useState(false);

  // Start / stop the camera.
  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setErrorMsg('This device or browser does not allow camera access here. Use “Take photo” or choose a file instead.');
        setStatus('error');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => { /* autoplay race — ignored */ });
        }
        setStatus('live');
      } catch {
        setErrorMsg('Camera permission was denied or no camera is available. Use “Take photo” or choose a file instead.');
        setStatus('error');
      }
    };
    void start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  // Sample the preview a few times a second and assess legibility.
  useEffect(() => {
    if (status !== 'live') return;
    const tick = () => {
      const video = videoRef.current;
      if (!video || video.videoWidth === 0) return;
      const longEdge = Math.max(video.videoWidth, video.videoHeight);
      const scale = Math.min(1, WORKING_LONG_EDGE / longEdge);
      const w = Math.max(1, Math.round(video.videoWidth * scale));
      const h = Math.max(1, Math.round(video.videoHeight * scale));
      const canvas = workCanvasRef.current ?? document.createElement('canvas');
      workCanvasRef.current = canvas;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, w, h);
      try {
        const data = ctx.getImageData(0, 0, w, h);
        setQuality(assessImageQuality({ data: data.data, width: w, height: h }, longEdge));
      } catch {
        /* tainted canvas — skip this sample */
      }
    };
    const id = window.setInterval(tick, SAMPLE_INTERVAL_MS);
    tick();
    return () => window.clearInterval(id);
  }, [status]);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    onCapture(dataUrl);
    setCaptured((n) => n + 1);
    setFlash(true);
    window.setTimeout(() => setFlash(false), 180);
  }, [onCapture]);

  const level = quality?.level ?? 'warn';
  const coachText =
    status !== 'live'
      ? ''
      : quality?.warnings[0] ?? 'Looks good — fill the frame with the board and capture.';
  const banner =
    level === 'good'
      ? { cls: 'bg-[var(--green)]/90', icon: <CheckCircle2 className="h-4 w-4" /> }
      : level === 'warn'
        ? { cls: 'bg-[var(--mustard)]/90', icon: <AlertTriangle className="h-4 w-4" /> }
        : { cls: 'bg-[var(--coral)]/90', icon: <AlertTriangle className="h-4 w-4" /> };

  return (
    <div className="fixed inset-0 z-[70] bg-black flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 text-white">
        <span className="flex items-center gap-2 text-sm font-medium">
          <Camera className="h-4 w-4" /> Live capture
          {captured > 0 && <span className="text-xs text-white/70">· {captured} captured</span>}
        </span>
        <button onClick={onClose} className="inline-flex items-center gap-1 text-sm hover:text-[var(--coral)]" title="Done (close)">
          <X className="h-4 w-4" /> Done
        </button>
      </div>

      {/* Camera area */}
      <div className="relative flex-1 overflow-hidden">
        <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-contain bg-black" />
        {flash && <div className="absolute inset-0 bg-[var(--bg-raised)]/70" />}

        {status === 'starting' && (
          <div className="absolute inset-0 flex items-center justify-center text-white/80 gap-2">
            <Loader2 className="h-5 w-5 animate-spin" /> Starting camera…
          </div>
        )}

        {status === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center p-8 text-center">
            <p className="text-sm text-white/80 max-w-sm">{errorMsg}</p>
          </div>
        )}

        {/* Live coaching banner */}
        {status === 'live' && (
          <div className={`absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm shadow-lg backdrop-blur ${banner.cls}`}>
            {banner.icon}
            <span className="max-w-[78vw] truncate">{coachText}</span>
          </div>
        )}
      </div>

      {/* Shutter */}
      {status === 'live' && (
        <div className="flex flex-col items-center gap-1 py-4">
          <button
            onClick={capture}
            title={level === 'good' ? 'Capture' : 'Capture anyway'}
            className={`h-16 w-16 rounded-full flex items-center justify-center ring-4 transition-colors ${
              level === 'good' ? 'bg-[var(--green)] ring-emerald-300' : level === 'warn' ? 'bg-[var(--bg-raised)] ring-white/60' : 'bg-[var(--bg-raised)] ring-red-300'
            }`}
          >
            <Check className={`h-7 w-7 ${level === 'good' ? 'text-white' : 'text-[var(--text-pri)]'}`} />
          </button>
          <span className="text-[11px] text-white/60">
            {level === 'good' ? 'Looks legible — capture' : 'You can capture anyway, but the read may be unreliable'}
          </span>
        </div>
      )}

      {status === 'error' && (
        <div className="py-4 text-center">
          <button onClick={onClose} className="px-4 py-1.5 bg-[var(--bg-raised)]/10 text-white text-sm hover:bg-[var(--bg-raised)]/20">Close</button>
        </div>
      )}
    </div>
  );
}

export default WhiteboardCaptureCoach;
